import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  ActionPriorityRatio,
  ActionsByMission,
  calculateCriticalBudgetRatio,
  calculateCriticalCompletedProjectsRatio,
  calculateOverdueProjectRatio,
  calculateProjectTypeBudgetRatios,
  countAppRelatedIncidentsRatio,
  countCriticalApplicationIncidentsRatioType,
  countCriticalDMZIncidentsRatioType,
  countCriticalNetworkIncidentsRatio,
  countCriticalPcIncidentsRatioType,
  countIncidentsByTypeForMission,
  countIncidentsCriticalOrHighRatioByMission,
  countIncidentsWithPerimeterNetworkRatio,
  countIncidentsWithPerimeterServerRatio,
  countIncidentsWithSlaUnder24Ratio,
  countProject,
  CriticalServerIncidentsRatioType,
  Mission,
  ProjectCountsByPriorityAndStatus,
  projectCountsByTypeAndStatus,
  projectPriority,
  projectPriorityCriticalRatio,
  ProjectsBudgetTable,
  VulnerabilityDistribution,
} from '../../types/missionTypes'
import {
  getAllMissions,
  deleteMissionApi,
  addMissionApi,
  getMissionbyIdApi,
  updateMissionApi,
  SearchApi,
  getProjectPriorityCriticalHighRatioeApi,
  getProjectPriorityHighRatioApi,
  getprojectPriorityCriticalRatioApi,
  getProjectCountsByPriorityApi,
  getProjectCountsByPriorityAndStatusApi,
  getProjectCountsByTypeAndStatusApi,
  getProjectsBudgetTableApi,
  calculateCriticalBudgetRatioByMissionIdApi,
  calculateProjectTypeBudgetRatiosApi,
  calculateCriticalCompletedProjectsRatioApi,
  calculateOverdueProjectRatioApi,
  countIncidentsCriticalOrHighRatioByMissionApi,
  countIncidentsByTypeForMissionApi,
  countIncidentsWithSlaUnder24RatioApi,
  countIncidentsWithPerimeterNetworkRatioApi,
  countIncidentsWithPerimeterServerApi,
  countCriticalNetworkIncidentsRatioApi,
  countAppRelatedIncidentsRatioApi,
  CriticalServerIncidentsRatioApi,
  countCriticalApplicationIncidentsRatioApi,
  countCriticalDMZIncidentsRatioApi,
  countCriticalPcIncidentsRatioApi,
  getVulnerabilityDistributionByMissionIdApi,
  getActionPriorityLowRatioApi,
  getActionPriorityMediumRatioApi,
  getActionPriorityHighRatioApi,
  getActionPriorityCriticalRatioApi,
  getCriticalActionsByMissionIdApi,
  getHighActionsByMissionIdApi,
  getMediumActionsByMissionIdApi,
  getLowActionsByMissionIdApi,
} from '../../api/missionsApi'
import { parseMissionFloatFields } from '../../utils/parseFloatFields'
import { handleError } from '../../utils/errorHandler'

export const getMissions = createAsyncThunk<
  {
    missions: Mission[]
    total: number
    totalPages: number
    currentPage?: number
    status?: number
    erreur?: boolean
    message?: string
  },
  { search?: string; page?: number; pageSize?: number },
  { rejectValue: string }
>('mission/getAllMissions', async ({ search, page, pageSize }, thunkAPI) => {
  try {
    const response = await getAllMissions({ search, page, pageSize })
    return {
      missions: response.missions,
      total: response.total,
      totalPages: response.totalPages ?? 0,
      currentPage: response.currentPage,
      status: response.status,
      message: response.message,
      erreur: response.erreur,
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const Search = createAsyncThunk<
  Mission[],
  {
    query: string
    field: string
    status?: number
    erreur?: string
    message?: boolean
  },
  { rejectValue: string }
>('mission/Search', async ({ query, field }, thunkAPI) => {
  try {
    const response = await SearchApi({ query, field })
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getMissionbyId = createAsyncThunk<
  Mission,
  string,
  { rejectValue: string }
>('mission/getMissionbyId', async (MissionId, thunkAPI) => {
  try {
    const response = await getMissionbyIdApi(MissionId)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const deleteMission = createAsyncThunk<
  Mission[],
  string,
  { rejectValue: string }
>('mission/deleteMission', async (MissionId, thunkAPI) => {
  try {
    const response: Mission[] = await deleteMissionApi(MissionId)
    return response // Ensure this matches the expected type
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const addMission = createAsyncThunk<
  Mission,
  Mission,
  { rejectValue: string }
>('mission/addMission', async (newMission, thunkAPI) => {
  try {
    // Parse float fields before sending to API
    const parsedMission = parseMissionFloatFields(newMission)
    const response = await addMissionApi(parsedMission)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const updateMission = createAsyncThunk<
  Mission,
  { updatedMission: Mission; _id: string },
  { rejectValue: string }
>('mission/updateMission', async ({ updatedMission, _id }, thunkAPI) => {
  try {
    // Parse float fields before sending to API
    const parsedMission = parseMissionFloatFields(updatedMission)
    const response = await updateMissionApi(parsedMission, _id)
    console.log(response)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getProjectPriorityCriticalHighRatio = createAsyncThunk<
  projectPriorityCriticalRatio,
  { idMission: string },
  { rejectValue: string }
>(
  'mission/getProjectPriorityCriticalHighRatio',
  async ({ idMission }, thunkAPI) => {
    try {
      const response = await getProjectPriorityCriticalHighRatioeApi(idMission)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)

export const getProjectPriorityHighRatio = createAsyncThunk<
  projectPriorityCriticalRatio,
  { idMission: string },
  { rejectValue: string }
>('mission/getProjectPriorityHighRatio', async ({ idMission }, thunkAPI) => {
  try {
    const response = await getProjectPriorityHighRatioApi(idMission)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getProjectPriorityCriticalRatio = createAsyncThunk<
  projectPriority,
  { idMission: string },
  { rejectValue: string }
>(
  'mission/getProjectPriorityCriticalRatio',
  async ({ idMission }, thunkAPI) => {
    try {
      const response = await getprojectPriorityCriticalRatioApi(idMission)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)

export const getprojetCountsByPriority = createAsyncThunk<
  countProject,
  { idMission: string },
  { rejectValue: string }
>('mission/getProjectCountsByPriority', async ({ idMission }, thunkAPI) => {
  try {
    const response = await getProjectCountsByPriorityApi(idMission)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getProjectCountsByPriorityAndStatus = createAsyncThunk<
  ProjectCountsByPriorityAndStatus,
  { idMission: string },
  { rejectValue: string }
>(
  'mission/getProjectCountsByPriorityAndStatus',
  async ({ idMission }, thunkAPI) => {
    try {
      const response = await getProjectCountsByPriorityAndStatusApi(idMission)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)

export const getProjectCountsByTypeAndStatus = createAsyncThunk<
  projectCountsByTypeAndStatus,
  { idMission: string },
  { rejectValue: string }
>(
  'mission/getProjectCountsByTypeAndStatus',
  async ({ idMission }, thunkAPI) => {
    try {
      const response = await getProjectCountsByTypeAndStatusApi(idMission)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)

export const getProjectsBudgetTable = createAsyncThunk<
  ProjectsBudgetTable,
  { idMission: string },
  { rejectValue: string }
>('mission/getProjectsBudgetTable', async ({ idMission }, thunkAPI) => {
  try {
    const response = await getProjectsBudgetTableApi(idMission)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const calculateCriticalBudgetRatioByMissionId = createAsyncThunk<
  calculateCriticalBudgetRatio,
  { idMission: string },
  { rejectValue: string }
>(
  'mission/calculateCriticalBudgetRatioByMissionId',
  async ({ idMission }, thunkAPI) => {
    try {
      const response =
        await calculateCriticalBudgetRatioByMissionIdApi(idMission)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to calculate Critical Budget Ratio By MissionId'
      )
    }
  }
)

export const CalculateCriticalBudgetRatioByMissionId = createAsyncThunk<
  calculateCriticalBudgetRatio,
  { idMission: string },
  { rejectValue: string }
>(
  'mission/calculateCriticalBudgetRatioByMissionId',
  async ({ idMission }, thunkAPI) => {
    try {
      const response =
        await calculateCriticalBudgetRatioByMissionIdApi(idMission)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to calculate Critical Budget Ratio By MissionId'
      )
    }
  }
)

export const CountIncidentsCriticalOrHighRatioByMission = createAsyncThunk<
  countIncidentsCriticalOrHighRatioByMission,
  { idMission: string },
  { rejectValue: string }
>(
  'mission/countIncidentsCriticalOrHighRatioByMission',
  async ({ idMission }, thunkAPI) => {
    try {
      const response =
        await countIncidentsCriticalOrHighRatioByMissionApi(idMission)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to calculate Critical Budget Ratio By MissionId'
      )
    }
  }
)

export const CalculateProjectTypeBudgetRatios = createAsyncThunk<
  calculateProjectTypeBudgetRatios,
  { idMission: string },
  { rejectValue: string }
>(
  'mission/calculateProjectTypeBudgetRatio',
  async ({ idMission }, thunkAPI) => {
    try {
      const response = await calculateProjectTypeBudgetRatiosApi(idMission)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to calculate Critical Budget Ratio By MissionId'
      )
    }
  }
)

export const CalculateCriticalCompletedProjectsRatio = createAsyncThunk<
  calculateCriticalCompletedProjectsRatio,
  { idMission: string },
  { rejectValue: string }
>(
  'mission/calculateCriticalCompletedProjectsRatio',
  async ({ idMission }, thunkAPI) => {
    try {
      const response =
        await calculateCriticalCompletedProjectsRatioApi(idMission)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to Calculate Critical Completed Projects Ratio'
      )
    }
  }
)

export const CalculateOverdueProjectRatio = createAsyncThunk<
  calculateOverdueProjectRatio,
  { idMission: string },
  { rejectValue: string }
>('mission/calculateOverdueProjectRatio', async ({ idMission }, thunkAPI) => {
  try {
    const response = await calculateOverdueProjectRatioApi(idMission)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const CountIncidentsByTypeForMission = createAsyncThunk<
  countIncidentsByTypeForMission,
  { idMission: string },
  { rejectValue: string }
>('mission/countIncidentsByTypeForMission', async ({ idMission }, thunkAPI) => {
  try {
    const response = await countIncidentsByTypeForMissionApi(idMission)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(
      'Failed to count Incidents By Type ForMission'
    )
  }
})

export const CountIncidentsWithSlaUnder24Ratio = createAsyncThunk<
  countIncidentsWithSlaUnder24Ratio,
  { idMission: string },
  { rejectValue: string }
>(
  'mission/countIncidentsWithSlaUnder24RatioApi',
  async ({ idMission }, thunkAPI) => {
    try {
      const response = await countIncidentsWithSlaUnder24RatioApi(idMission)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to count Incidents With SlaUnder 24Ratio'
      )
    }
  }
)

export const CountIncidentsWithPerimeterNetworkRatio = createAsyncThunk<
  countIncidentsWithPerimeterNetworkRatio,
  { idMission: string },
  { rejectValue: string }
>(
  'mission/countIncidentsWithPerimeterNetworkRatio',
  async ({ idMission }, thunkAPI) => {
    try {
      const response =
        await countIncidentsWithPerimeterNetworkRatioApi(idMission)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to count Incidents With Perimeter Network Ratio'
      )
    }
  }
)

export const CountIncidentsWithPerimeterServer = createAsyncThunk<
  countIncidentsWithPerimeterServerRatio,
  { idMission: string },
  { rejectValue: string }
>(
  'mission/countIncidentsWithPerimeterServerRatio',
  async ({ idMission }, thunkAPI) => {
    try {
      const response = await countIncidentsWithPerimeterServerApi(idMission)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to count Incidents With Perimeter ServerRatio'
      )
    }
  }
)

export const CriticalServerIncidentsRatio = createAsyncThunk<
  CriticalServerIncidentsRatioType,
  { idMission: string },
  { rejectValue: string }
>('mission/CriticalServerIncidentsRatio', async ({ idMission }, thunkAPI) => {
  try {
    const response = await CriticalServerIncidentsRatioApi(idMission)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(
      'Failed to count Critical Network Incidents Ratio'
    )
  }
})

export const countCriticalPcIncidentsRatio = createAsyncThunk<
  countCriticalPcIncidentsRatioType,
  { idMission: string },
  { rejectValue: string }
>('mission/countCriticalPC', async ({ idMission }, thunkAPI) => {
  try {
    const response = await countCriticalPcIncidentsRatioApi(idMission)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(
      'Failed to count Critical PC Incidents Ratio'
    )
  }
})

export const countCriticalDMZIncidentsRatio = createAsyncThunk<
  countCriticalDMZIncidentsRatioType,
  { idMission: string },
  { rejectValue: string }
>('mission/countCritical', async ({ idMission }, thunkAPI) => {
  try {
    const response = await countCriticalDMZIncidentsRatioApi(idMission)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(
      'Failed to count Critical DMZ Incidents Ratio'
    )
  }
})

export const countCriticalApplicationIncidentsRatio = createAsyncThunk<
  countCriticalApplicationIncidentsRatioType,
  { idMission: string },
  { rejectValue: string }
>(
  'mission/countCriticalApplicationIncidentsRatio',
  async ({ idMission }, thunkAPI) => {
    try {
      const response =
        await countCriticalApplicationIncidentsRatioApi(idMission)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to count Critical Application Incidents Ratio'
      )
    }
  }
)

export const CountCriticalNetworkIncidentsRatio = createAsyncThunk<
  countCriticalNetworkIncidentsRatio,
  { idMission: string },
  { rejectValue: string }
>(
  'mission/countCriticalNetworkIncidentsRatio',
  async ({ idMission }, thunkAPI) => {
    try {
      const response = await countCriticalNetworkIncidentsRatioApi(idMission)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to count Critical Network Incidents Ratio'
      )
    }
  }
)

export const CountAppRelatedIncidentsRatio = createAsyncThunk<
  countAppRelatedIncidentsRatio,
  { idMission: string },
  { rejectValue: string }
>('mission/CountAppRelatedIncidentsRatio', async ({ idMission }, thunkAPI) => {
  try {
    const response = await countAppRelatedIncidentsRatioApi(idMission)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(
      'Failed to count App Related Incidents Ratio'
    )
  }
})
export const GetVulnerabilityDistributionByMissionId = createAsyncThunk<
  VulnerabilityDistribution,
  { idMission: string },
  { rejectValue: string }
>(
  'mission/getVulnerabilityDistributionByMissionId',
  async ({ idMission }, thunkAPI) => {
    try {
      const response =
        await getVulnerabilityDistributionByMissionIdApi(idMission)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(
        'Failed to count App Related Incidents Ratio'
      )
    }
  }
)
export const fetchActionPriorityCriticalRatio = createAsyncThunk<
  ActionPriorityRatio,
  string,
  { rejectValue: string }
>('actionPriority/fetchCriticalRatio', async (id, thunkAPI) => {
  try {
    return await getActionPriorityCriticalRatioApi(id)
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const fetchActionPriorityHighRatio = createAsyncThunk<
  ActionPriorityRatio,
  string,
  { rejectValue: string }
>('actionPriority/fetchHighRatio', async (id, thunkAPI) => {
  try {
    return await getActionPriorityHighRatioApi(id)
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const fetchActionPriorityMediumRatio = createAsyncThunk<
  ActionPriorityRatio,
  string,
  { rejectValue: string }
>('actionPriority/fetchMediumRatio', async (id, thunkAPI) => {
  try {
    return await getActionPriorityMediumRatioApi(id)
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const fetchActionPriorityLowRatio = createAsyncThunk<
  ActionPriorityRatio,
  string,
  { rejectValue: string }
>('actionPriority/fetchLowRatio', async (id, thunkAPI) => {
  try {
    return await getActionPriorityLowRatioApi(id)
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})
export const fetchCriticalActionsByMissionId = createAsyncThunk<
  ActionsByMission,
  string,
  { rejectValue: string }
>('actionsByMission/fetchCritical', async (id, thunkAPI) => {
  try {
    return await getCriticalActionsByMissionIdApi(id)
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const fetchHighActionsByMissionId = createAsyncThunk<
  ActionsByMission,
  string,
  { rejectValue: string }
>('actionsByMission/fetchHigh', async (id, thunkAPI) => {
  try {
    return await getHighActionsByMissionIdApi(id)
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const fetchMediumActionsByMissionId = createAsyncThunk<
  ActionsByMission,
  string,
  { rejectValue: string }
>('actionsByMission/fetchMedium', async (id, thunkAPI) => {
  try {
    return await getMediumActionsByMissionIdApi(id)
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const fetchLowActionsByMissionId = createAsyncThunk<
  ActionsByMission,
  string,
  { rejectValue: string }
>('actionsByMission/fetchLow', async (id, thunkAPI) => {
  try {
    return await getLowActionsByMissionIdApi(id)
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})
